import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarClock, Hash, Heart, MessageSquareText, Package, RefreshCw, SendHorizontal, Star, User } from "lucide-react";

import Panel from "../components/Panel";
import { businessProductsAPI } from "../services/businessProductsService";
import ReviewMediaGallery from "../../components/reviews/ReviewMediaGallery";
import { normalizeReviewMedia } from "../../utils/reviewMedia";
import { Button, PageHeader } from "../../components/ui";

function safeDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-PH", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function normalizeReview(review, index) {
  return {
    id: review?.id ?? `review-${index}`,
    productId: review?.productId ?? review?.product_id ?? null,
    orderItemId: review?.orderItemId ?? review?.order_item_id ?? null,
    productName: review?.productName || review?.product_name || "Product",
    productImage: review?.productImage || review?.product_image || review?.image || review?.imageUrl || null,
    shopperName: review?.shopperName || review?.shopper_name || "Shopper",
    orderReference: review?.orderReference || review?.order_reference || null,
    rating: Number(review?.rating || 0),
    reviewText: review?.reviewText || review?.review_text || "",
    media: normalizeReviewMedia(review?.media, review?.photos),
    likeCount: Number(review?.likeCount || review?.like_count || 0),
    createdAt: review?.createdAt || review?.created_at || null,
    sellerReplyText: review?.sellerReplyText || review?.seller_reply_text || "",
    sellerReplyAt: review?.sellerReplyAt || review?.seller_reply_at || null,
    sellerReplyBusinessName: review?.sellerReplyBusinessName || review?.seller_reply_business_name || "",
  };
}

function RatingDisplay({ rating }) {
  const safeRating = Math.max(0, Math.min(5, Number(rating || 0)));

  return (
    <div className="inline-flex items-center gap-2">
      <div className="flex items-center gap-0.5 text-amber-400">
        {[1, 2, 3, 4, 5].map((value) => (
          <Star
            key={value}
            size={13}
            fill={value <= safeRating ? "currentColor" : "none"}
            className={value <= safeRating ? "" : "text-neutral-300 dark:text-neutral-700"}
          />
        ))}
      </div>
      <span className="text-xs font-semibold text-neutral-700 dark:text-neutral-200">
        {safeRating}/5
      </span>
    </div>
  );
}

export default function BusinessReviews() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [reviews, setReviews] = useState([]);
  const [openReviewId, setOpenReviewId] = useState(null);
  const [replyDraft, setReplyDraft] = useState("");
  const [replyError, setReplyError] = useState("");
  const [replySubmitting, setReplySubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const payload = await businessProductsAPI.listReviews();
      const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.items)
          ? payload.items
          : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload?.data?.items)
              ? payload.data.items
              : [];

      setReviews(rows.map(normalizeReview));
    } catch (err) {
      setReviews([]);
      setError(err?.message || "Failed to load reviews.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openReview = useMemo(
    () => reviews.find((review) => String(review.id) === String(openReviewId)) || null,
    [reviews, openReviewId]
  );

  useEffect(() => {
    if (openReviewId == null) return;
    if (openReview) return;
    setOpenReviewId(null);
  }, [openReview, openReviewId]);

  useEffect(() => {
    setReplyDraft("");
    setReplyError("");
    setReplySubmitting(false);
  }, [openReviewId]);

  const filteredReviews = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return reviews;

    return reviews.filter((review) =>
      [
        review.productName,
        review.shopperName,
        review.orderReference,
        review.reviewText,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(normalizedQuery)
    );
  }, [query, reviews]);

  const submitReply = useCallback(async (event) => {
    event.preventDefault();
    if (!openReview) return;

    const normalizedReply = replyDraft.trim();
    if (!normalizedReply) {
      setReplyError("Reply is required.");
      return;
    }

    setReplySubmitting(true);
    setReplyError("");

    try {
      const payload = await businessProductsAPI.replyToReview(openReview.id, { replyText: normalizedReply });
      const rawUpdated = payload?.data || payload || null;
      const updatedReview = rawUpdated ? normalizeReview(rawUpdated, 0) : {
        ...openReview,
        sellerReplyText: normalizedReply,
        sellerReplyAt: new Date().toISOString(),
      };

      setReviews((current) =>
        current.map((review) =>
          String(review.id) === String(openReview.id)
            ? { ...review, ...updatedReview }
            : review
        )
      );

      setReplyDraft("");
    } catch (err) {
      setReplyError(err?.message || "Failed to submit seller reply.");
    } finally {
      setReplySubmitting(false);
    }
  }, [openReview, replyDraft]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Feedback"
        description="Reviews shoppers left on your products, and your replies."
        actions={<Button size="sm" icon={RefreshCw} onClick={load}>Refresh</Button>}
      />
      <Panel
        title="All feedback"
        right={(
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search product, shopper, order"
              className="w-full rounded-xl border border-neutral-200 bg-transparent px-3 py-2 text-sm text-neutral-900 outline-none transition placeholder:text-neutral-400 focus:border-neutral-400 focus:ring-2 focus:ring-neutral-200 dark:border-neutral-700 dark:text-neutral-100 dark:placeholder:text-neutral-500 dark:focus:border-neutral-600 dark:focus:ring-neutral-800 sm:w-80"
            />
          </div>
        )}
      >
        {loading ? (
          <div className="space-y-3 rounded-2xl border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-800 dark:bg-neutral-900/50">
            {Array.from({ length: 5 }).map((_, index) => (
              <div
                key={index}
                className="h-4 animate-pulse rounded-lg bg-neutral-200 dark:bg-neutral-800"
              />
            ))}
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/40 dark:text-rose-200">
            {error}
          </div>
        ) : filteredReviews.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-2xl border border-neutral-200 bg-neutral-50 px-5 py-12 text-center dark:border-neutral-800 dark:bg-neutral-900/50">
            <MessageSquareText size={40} className="text-neutral-300 dark:text-neutral-700" />
            <div className="space-y-1">
              <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                No reviews yet
              </p>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                Shopper reviews for your products will appear here once completed orders are rated.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="hidden overflow-x-auto rounded-2xl border border-neutral-200 dark:border-neutral-800 sm:block">
              <table className="w-full min-w-[860px] border-collapse bg-white dark:bg-neutral-900">
                <thead className="bg-neutral-50 dark:bg-neutral-900/80">
                  <tr className="border-b border-neutral-200 dark:border-neutral-800">
                    <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Product</th>
                    <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Shopper</th>
                    <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Rating</th>
                    <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Review</th>
                    <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Order</th>
                    <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReviews.map((review) => (
                    <tr
                      key={review.id}
                      className="cursor-pointer border-b border-neutral-200 align-top transition hover:bg-neutral-50/70 dark:border-neutral-800 dark:hover:bg-neutral-800/40"
                      onClick={() => setOpenReviewId(review.id)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setOpenReviewId(review.id);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label={`Open review details for ${review.productName}`}
                    >
                      <td className="p-3 text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                        {review.productName}
                      </td>
                      <td className="p-3 text-sm text-neutral-600 dark:text-neutral-300">
                        {review.shopperName}
                      </td>
                      <td className="p-3 text-sm text-neutral-600 dark:text-neutral-300">
                        <RatingDisplay rating={review.rating} />
                      </td>
                      <td className="p-3 text-sm text-neutral-600 dark:text-neutral-300">
                        <div className="max-w-[320px] whitespace-pre-wrap break-words">
                          {review.reviewText || "No review text."}
                        </div>
                        <ReviewMediaGallery
                          media={review.media}
                          className="mt-2"
                        />
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs font-medium text-neutral-500 dark:text-neutral-400">
                          <span className={`inline-flex items-center rounded-full border px-2.5 py-1 ${
                            review.sellerReplyText
                              ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-200"
                              : "border-neutral-200 bg-white text-neutral-500 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-400"
                          }`}>
                            {review.sellerReplyText ? "Seller replied" : "No seller reply yet"}
                          </span>
                          <span className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-neutral-600 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300">
                            <Heart size={11} className="text-rose-500 dark:text-rose-400" />
                            {review.likeCount} likes
                          </span>
                        </div>
                      </td>
                      <td className="p-3 text-sm text-neutral-600 dark:text-neutral-300">
                        {review.orderReference || "—"}
                      </td>
                      <td className="p-3 text-sm text-neutral-600 dark:text-neutral-300">
                        {safeDateTime(review.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="space-y-3 sm:hidden">
              {filteredReviews.map((review) => (
                <article
                  key={review.id}
                  className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm transition active:scale-[0.998] dark:border-neutral-800 dark:bg-neutral-900"
                  onClick={() => setOpenReviewId(review.id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setOpenReviewId(review.id);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Open review details for ${review.productName}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                        {review.productName}
                      </p>
                      <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                        {review.shopperName}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3">
                    <RatingDisplay rating={review.rating} />
                  </div>

                  <p className="mt-3 whitespace-pre-wrap text-sm text-neutral-600 dark:text-neutral-300">
                    {review.reviewText || "No review text."}
                  </p>
                  <ReviewMediaGallery
                    media={review.media}
                    className="mt-3"
                  />

                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs font-medium text-neutral-500 dark:text-neutral-400">
                    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 ${
                      review.sellerReplyText
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-200"
                        : "border-neutral-200 bg-white text-neutral-500 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-400"
                    }`}>
                      {review.sellerReplyText ? "Seller replied" : "No seller reply yet"}
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-neutral-600 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300">
                      <Heart size={11} className="text-rose-500 dark:text-rose-400" />
                      {review.likeCount} likes
                    </span>
                  </div>

                  <div className="mt-3 grid grid-cols-1 gap-2 text-xs text-neutral-500 dark:text-neutral-400">
                    <div>Order: {review.orderReference || "—"}</div>
                    <div>Submitted: {safeDateTime(review.createdAt)}</div>
                  </div>
                </article>
              ))}
            </div>
          </>
        )}
      </Panel>

      <div
        className={`fixed inset-0 z-[55] ${openReview ? "pointer-events-auto" : "pointer-events-none"}`}
        aria-hidden={!openReview}
      >
        <div
          className={`absolute inset-0 bg-black/40 transition ${openReview ? "opacity-100" : "opacity-0"}`}
          onClick={() => setOpenReviewId(null)}
        />

        <aside
          className={
            "absolute right-0 top-0 h-full w-full border-l border-zinc-200 bg-white shadow-2xl transition-transform dark:border-zinc-800 dark:bg-[#0b0b0c] sm:w-[460px] " +
            (openReview ? "translate-x-0" : "translate-x-full")
          }
        >
          <header className="flex items-center justify-between border-b border-zinc-200 px-4 py-3 dark:border-zinc-800">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-400">
                Review Details
              </div>
              <div className="mt-1 text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                {openReview?.productName || "Product"}
              </div>
            </div>

            <button
              type="button"
              onClick={() => setOpenReviewId(null)}
              className="rounded-xl border border-zinc-200 px-3 py-2 text-sm font-medium text-zinc-700 transition hover:bg-zinc-50 dark:border-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-900/60"
            >
              Close
            </button>
          </header>

          <div className="h-[calc(100%-72px)] space-y-4 overflow-y-auto p-4">
            <div className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-[#121214]">
              {openReview?.productImage ? (
                <img
                  src={openReview.productImage}
                  alt={openReview.productName || "Product"}
                  className="mb-3 h-28 w-28 rounded-xl border border-zinc-200 object-cover dark:border-zinc-800"
                />
              ) : null}

              <div className="space-y-2 text-sm text-zinc-700 dark:text-zinc-200">
                <div className="flex items-start gap-2">
                  <Package size={15} className="mt-0.5 shrink-0 text-zinc-400 dark:text-zinc-500" />
                  <div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400">Product</div>
                    <div className="font-medium text-zinc-900 dark:text-zinc-100">{openReview?.productName || "—"}</div>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <User size={15} className="mt-0.5 shrink-0 text-zinc-400 dark:text-zinc-500" />
                  <div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400">Shopper</div>
                    <div className="font-medium text-zinc-900 dark:text-zinc-100">{openReview?.shopperName || "—"}</div>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <Hash size={15} className="mt-0.5 shrink-0 text-zinc-400 dark:text-zinc-500" />
                  <div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400">Order</div>
                    <div className="font-medium text-zinc-900 dark:text-zinc-100">{openReview?.orderReference || "—"}</div>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <CalendarClock size={15} className="mt-0.5 shrink-0 text-zinc-400 dark:text-zinc-500" />
                  <div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400">Submitted</div>
                    <div className="font-medium text-zinc-900 dark:text-zinc-100">{safeDateTime(openReview?.createdAt)}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-[#121214]">
              <div className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-400">
                Rating
              </div>
              <div className="mt-2">
                <RatingDisplay rating={openReview?.rating || 0} />
              </div>
              <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-600 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-300">
                <Heart size={12} />
                <span>{openReview?.likeCount || 0} likes</span>
              </div>
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-[#121214]">
              <div className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-400">
                Review
              </div>
              <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-zinc-700 dark:text-zinc-200">
                {openReview?.reviewText || "No review text provided."}
              </p>
              <ReviewMediaGallery
                media={openReview?.media}
                className="mt-3"
              />
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-[#121214]">
              <div className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-400">
                Seller Reply
              </div>

              {openReview?.sellerReplyText ? (
                <div className="mt-3 rounded-xl border border-zinc-200 bg-zinc-50 p-3 dark:border-zinc-700 dark:bg-zinc-900/70">
                  <div className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                    {openReview?.sellerReplyBusinessName || "Seller"}
                    {openReview?.sellerReplyAt ? ` • ${safeDateTime(openReview.sellerReplyAt)}` : ""}
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-zinc-700 dark:text-zinc-200">
                    {openReview.sellerReplyText}
                  </p>
                </div>
              ) : (
                <form className="mt-3 space-y-3" onSubmit={submitReply}>
                  <textarea
                    value={replyDraft}
                    onChange={(event) => setReplyDraft(event.target.value)}
                    placeholder="Write a reply to this shopper review..."
                    rows={4}
                    maxLength={1000}
                    className="w-full rounded-xl border border-zinc-200 bg-transparent px-3 py-2 text-sm text-zinc-900 outline-none transition placeholder:text-zinc-400 focus:border-zinc-400 focus:ring-2 focus:ring-zinc-200 dark:border-zinc-700 dark:text-zinc-100 dark:placeholder:text-zinc-500 dark:focus:border-zinc-600 dark:focus:ring-zinc-800"
                  />
                  {replyError ? (
                    <p className="text-xs text-rose-600 dark:text-rose-300">
                      {replyError}
                    </p>
                  ) : null}
                  <button
                    type="submit"
                    disabled={replySubmitting || replyDraft.trim().length === 0}
                    className="inline-flex items-center gap-2 rounded-xl bg-zinc-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-zinc-200"
                  >
                    <SendHorizontal size={14} />
                    {replySubmitting ? "Sending..." : "Send Reply"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
